/**
 * Admin Pricing Page
 *
 * Director sets the monthly price of classes by scope (level / class type /
 * specific classes). Only affects NEW modules — existing modules/payments are
 * untouched. Same scope pattern as Extra Materials.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SearchableSelect, Spinner } from "../../components/ui";
import type { SearchableSelectItem } from "../../components/ui";
import {
  useAdminClassGroups,
  useAdminLevels,
  useBulkPriceClasses,
} from "../../hooks/useAdmin";
import type { ClassPriceScope } from "../../types/admin";

const CLASS_TYPES = [
  { value: "group", label: "Group" },
  { value: "duo", label: "Duo" },
  { value: "individual", label: "Individual" },
];

export function AdminPricingPage() {
  const { data: classes, isLoading } = useAdminClassGroups();
  const { data: levels } = useAdminLevels();
  const bulkPrice = useBulkPriceClasses();

  const [scope, setScope] = useState<ClassPriceScope>("level");
  const [value, setValue] = useState("");
  const [levelCode, setLevelCode] = useState("");
  const [classType, setClassType] = useState("group");
  const [classGroups, setClassGroups] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Current-prices list filters
  const [listSearch, setListSearch] = useState("");
  const [listLevel, setListLevel] = useState("");
  const [listType, setListType] = useState("");

  const classItems = useMemo<SearchableSelectItem[]>(
    () =>
      (classes ?? []).map((c) => ({
        id: c.id,
        label: `${c.name} (${c.levelCode})`,
        searchText: `${c.name} ${c.levelCode}`,
      })),
    [classes],
  );

  const canApply =
    Number(value) > 0 &&
    (scope === "level"
      ? !!levelCode
      : scope === "class_type"
        ? !!classType
        : classGroups.length > 0);

  // How many active classes the change will hit (for the confirmation modal).
  const affectedCount = useMemo(() => {
    const list = classes ?? [];
    if (scope === "level") return list.filter((c) => c.levelCode === levelCode).length;
    if (scope === "class_type") return list.filter((c) => c.classType === classType).length;
    return classGroups.length;
  }, [classes, scope, levelCode, classType, classGroups]);

  const handleConfirm = () => {
    const numeric = Number(value);
    if (!numeric || numeric <= 0) return;
    bulkPrice.mutate(
      {
        scope,
        value: numeric,
        level: scope === "level" ? levelCode : undefined,
        classType:
          scope === "class_type"
            ? (classType as "individual" | "duo" | "group")
            : undefined,
        classGroups: scope === "class" ? classGroups : undefined,
      },
      { onSuccess: () => setConfirmOpen(false) },
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const visibleClasses = [...(classes ?? [])]
    .filter((c) => {
      if (listLevel && c.levelCode !== listLevel) return false;
      if (listType && c.classType !== listType) return false;
      if (
        listSearch &&
        !c.name.toLowerCase().includes(listSearch.toLowerCase())
      )
        return false;
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
            Class Pricing
          </h1>
          <p className="text-earth-700 text-sm mt-1">
            Set the monthly price by scope. Applies to new modules only —
            existing students' payments are not changed.
          </p>
        </div>

        {/* Apply form */}
        <div className="bg-white rounded-xl border border-earth-200 p-4 mb-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-earth-700 mb-1">
                Scope
              </label>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as ClassPriceScope)}
                className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
              >
                <option value="level">All classes of a level</option>
                <option value="class_type">By type (group/duo/individual)</option>
                <option value="class">Specific classes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-earth-700 mb-1">
                Monthly price (BRL)
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="250.00"
                className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
              />
            </div>
          </div>

          {scope === "level" && (
            <div>
              <label className="block text-sm font-medium text-earth-700 mb-1">
                Level
              </label>
              <select
                value={levelCode}
                onChange={(e) => setLevelCode(e.target.value)}
                className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
              >
                <option value="">Select a level</option>
                {levels?.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.code}
                  </option>
                ))}
              </select>
            </div>
          )}

          {scope === "class_type" && (
            <div>
              <label className="block text-sm font-medium text-earth-700 mb-1">
                Type
              </label>
              <select
                value={classType}
                onChange={(e) => setClassType(e.target.value)}
                className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
              >
                {CLASS_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {scope === "class" && (
            <div>
              <label className="block text-sm font-medium text-earth-700 mb-1">
                Classes
              </label>
              <SearchableSelect
                items={classItems.filter((i) => !classGroups.includes(i.id))}
                value=""
                onChange={(id) => {
                  if (id) setClassGroups((prev) => [...prev, id]);
                }}
                emptyLabel="Add a class..."
              />
              {classGroups.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {classGroups.map((id) => {
                    const item = classItems.find((c) => c.id === id);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-olive-100 text-olive-700 rounded text-xs font-medium"
                      >
                        {item?.label ?? id}
                        <button
                          type="button"
                          onClick={() =>
                            setClassGroups((prev) =>
                              prev.filter((x) => x !== id),
                            )
                          }
                          className="hover:text-olive-900"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => setConfirmOpen(true)}
            disabled={!canApply}
            className="px-4 py-2.5 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            Apply price
          </button>
        </div>

        {/* Current prices */}
        <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-earth-100 text-xs font-semibold text-earth-700 uppercase tracking-wide">
            Current class prices
          </div>

          {/* List filters */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 border-b border-earth-100">
            <div className="relative flex-1 min-w-[160px]">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-earth-400"></i>
              <input
                type="text"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Search class..."
                className="w-full pl-10 pr-4 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
              />
            </div>
            <select
              value={listLevel}
              onChange={(e) => setListLevel(e.target.value)}
              className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
            >
              <option value="">All levels</option>
              {levels?.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.code}
                </option>
              ))}
            </select>
            <select
              value={listType}
              onChange={(e) => setListType(e.target.value)}
              className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
            >
              <option value="">All types</option>
              {CLASS_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="divide-y divide-earth-100">
            {visibleClasses.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between px-4 py-3"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="px-2 py-0.5 bg-olive-100 text-olive-700 rounded text-xs font-semibold shrink-0">
                    {c.levelCode}
                  </span>
                  <span className="text-sm text-earth-800 truncate">
                    {c.name}
                  </span>
                  <span className="text-xs text-earth-700 capitalize">
                    {c.classType}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.pricePerMonth != null && (
                    <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-medium">
                      custom
                    </span>
                  )}
                  <span className="text-sm font-medium text-earth-700">
                    R$ {Number(c.effectivePrice).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
            {visibleClasses.length === 0 && (
              <div className="text-center py-8 text-earth-700 text-sm">
                No classes match the filters.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setConfirmOpen(false)}
          ></div>
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-money-dollar-circle-line text-xl text-emerald-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-earth-800 mb-2">
              Apply new price?
            </h3>
            <p className="text-sm text-earth-700 mb-2">
              Set{" "}
              <strong className="text-earth-800">
                R$ {Number(value || 0).toFixed(2)}/month
              </strong>{" "}
              on{" "}
              <strong className="text-earth-800">
                {affectedCount} class{affectedCount !== 1 ? "es" : ""}
              </strong>
              {scope === "level"
                ? ` of level ${levelCode}`
                : scope === "class_type"
                  ? ` of type ${classType}`
                  : ""}
              .
            </p>
            <p className="text-xs text-earth-700 mb-6">
              Applies to new modules only — existing payments are not changed.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleConfirm}
                disabled={bulkPrice.isPending}
                className="flex-1 px-4 py-2.5 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {bulkPrice.isPending ? "Applying..." : "Confirm"}
              </button>
              <button
                onClick={() => setConfirmOpen(false)}
                className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPricingPage;
